import React, { FC, useEffect } from "react"
import ArticleSideBar from "./ArticleSideBar"
import Moment from "react-moment"
import { GatsbyImage, IGatsbyImageData } from "gatsby-plugin-image"
import { Social, SUPPORTED_SOCIAL } from "./Social"
import {
  Link,
  useI18next,
  useTranslation,
} from "../../plugins/i18next/ea-react-i18next"
import { ArticleNode } from "../../utils/core"

const ArticleDetailsContent: FC<{ article: ArticleNode }> = ({ article }) => {
  const { t } = useTranslation("", {
    keyPrefix: "article.articledetailscontent",
  })
  const { i18n } = useI18next()
  const currentLanguage = i18n.language

  if (!article) {
    return <React.Fragment></React.Fragment>
  }

  const { frontmatter, html } = article
  const { title, date, category, blogimage, slug, author, seriesarticles } =
    frontmatter

  const serieslabel = frontmatter?.serieslabel
  const isSeries = !!serieslabel
  const path =
    category?.toLowerCase() === "story" ? "stories" : category?.toLowerCase()

  let targetTitle = title
  if (isSeries) {
    targetTitle = title.replace(serieslabel, "").trim()
    if (targetTitle.startsWith("-")) {
      targetTitle = targetTitle.substring(1).trim()
    }
  }

    useEffect(() => {
      if (typeof window === "undefined") return

      document.body.classList.remove("lang-en", "lang-de")
      document.body.classList.add(`lang-${currentLanguage}`)
    }, [])

  return (
    <section className="blog-details-area" style={{ paddingTop: 200 }}>
      <div className="container-fluid hero-container">
        <div className="row">
          <div className="col-lg-8 col-md-12">
            <div className="blog-details-desc">
              <div className="article-image">
                {blogimage?.childImageSharp?.gatsbyImageData && (
                  <GatsbyImage
                    image={
                      blogimage?.childImageSharp
                        ?.gatsbyImageData as unknown as IGatsbyImageData
                    }
                    alt={blogimage?.alternativeText ?? ""}
                  />
                )}
              </div>
              <div className="article-content">
                <div className="entry-meta">
                  <ul>
                    <li>
                      <i className="bx bx-folder-open"></i>
                      <span>{t("category")}</span>
                      {category}
                    </li>
                    <li>
                      <i className="bx bx-calendar"></i>
                      <span>{t("lastupdated")}</span>
                      <Moment format="L">{date}</Moment>
                    </li>
                  </ul>
                </div>
                {isSeries && (
                  <React.Fragment>
                    <div className="serieslabel">
                      <h4>{t("series")}</h4>
                    </div>
                    <div className="seriestitle">
                      <h5>{serieslabel}</h5>
                      <ol style={{ listStyleType: "none" }}>
                        {seriesarticles?.map((part, index) => (
                          <li key={index}>
                            {title === part?.frontmatter.title ? (
                              <h6>
                                {index + 1}: {part?.frontmatter.title}{" "}
                                <i>({t("currentArticle")})</i>
                              </h6>
                            ) : (
                              <Link
                                className="seriestitle"
                                to={`/${path}/${part?.frontmatter.slug}`}
                              >
                                <h6>
                                  {index + 1}: {part?.frontmatter.title}
                                </h6>
                              </Link>
                            )}
                          </li>
                        ))}
                      </ol>
                    </div>
                  </React.Fragment>
                )}
                <h1 className="story-title">{targetTitle}</h1>
                <div dangerouslySetInnerHTML={{ __html: html }} />
              </div>
              <div className="article-footer">
                <div className="article-tags" />
                <div className="article-share">
                  <ul className="social">
                    <li>
                      <span>{t("share") + ":"}</span>
                    </li>
                    <li>
                      <Social
                        title={title}
                        type={SUPPORTED_SOCIAL.X}
                        shareLink={`https://www.easylife365.cloud/stories/${slug}`}
                      />
                    </li>
                    <li>
                      <Social
                        title={title}
                        type={SUPPORTED_SOCIAL.LINKEDIN}
                        shareLink={`https://www.easylife365.cloud/stories/${slug}`}
                      />
                    </li>
                  </ul>
                </div>
              </div>
              {author?.authorname && (
                <div className="article-author">
                  <div className="author-profile-header"></div>
                  <div className="author-profile">
                    <div className="author-profile-title">
                      <div className="imageWrapper">
                        {author?.authorImage && (
                          <GatsbyImage
                            image={
                              author?.authorImage?.childImageSharp
                                .gatsbyImageData as unknown as IGatsbyImageData
                            }
                            alt={
                              author?.authorImage?.childImageSharp
                                .gatsbyImageData.alt as string
                            }
                          />
                        )}
                      </div>
                      <h4>{author?.authorname}</h4>
                      <p>
                        <div
                          dangerouslySetInnerHTML={{
                            __html: author?.description as string,
                          }}
                        />
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="col-lg-4 col-md-12">
            <ArticleSideBar currentSlug={slug} />
          </div>
        </div>
      </div>
    </section>
  )
}

export default ArticleDetailsContent
